/**
 * Landing page static data arrays.
 * All content moved from app/page.tsx for better organization.
 */

import type { ReactNode } from "react";
import Link from "next/link";

const faqLink =
  "underline decoration-[#ffd372] decoration-2 underline-offset-2 text-[#2d2d2d] font-semibold hover:text-[#c0392b] transition-colors";

export const carouselData = [
  {
    id: 1,
    title: "Daily Drip",
    description:
      "Stop the 'what to wear' panic. This is your personal daily outfit generator. Just tell me your mood and plans, and I'll serve three killer looks from your own closet. You're welcome.",
    image: "/landing-page/features-section/Card_daily-drip.webp",
    altText: "Daily Drip - your personal daily outfit generator",
    bgColor: "#f5a0c0",
  },
  {
    id: 2,
    title: "Look Lab",
    description:
      "Your personal style sandbox. This is where you mix and match outfits from your own closet. Let's find those iconic outfit combinations you never even knew you had.",
    image: "/landing-page/features-section/Card_look-lab.webp",
    altText: "Look Lab - match outfits from your own closet",
    bgColor: "#ffd372",
  },
  {
    id: 3,
    title: "Your Pods",
    description:
      "Create your own custom closets (We call them 'pods'). Use this as your capsule wardrobe builder for any vibe, trip, or mood. Your 'Sad Girl Autumn' pod is waiting.",
    image: "/landing-page/features-section/Card_your-pods.webp",
    altText: "Your Pods - your own custom digital closet",
    bgColor: "#89bff8",
  },
  {
    id: 4,
    title: "Shopping Quest",
    description:
      "Your brutally honest AI shopping assistant. Use our image search shopping for a live 'vibe check' in-store, or let me find the one new piece your current wardrobe is actually missing.",
    image: "/landing-page/features-section/Card_shopping-quest.webp",
    altText: "Shopping Quest - AI shopping assistant for your wardrobe",
    bgColor: "#ffd372",
  },
];

export const testimonialsData = [
  {
    id: 1,
    review:
      "Honestly, Hoku saved my mornings. I used to have a minor mental breakdown staring at my closet before my 8 AM class. Now I just tap my vibe and it gives me options I actually own. I haven't been late in weeks… it's a new record.",
    name: "Priya C.S.",
    details: "PSG CAS (BBA), Coimbatore",
    bgColor: "#f0ead8",
  },
  {
    id: 2,
    review:
      "I'm obsessed with thrifting, but I end up with so many random pieces. I had this one floral skirt I loved but never wore nd I uploaded it to Hoku nd it gave me 3 new combos I'd never have thought of only using tops I already had. It's like thrifting in my own closet.",
    name: "Aanya Ravikumar",
    details: "Growth Marketer (Edstat), Bangalore",
    bgColor: "#f0ead8",
  },
  {
    id: 3,
    review:
      "I was very skeptical, it was an AI after all, how accurate will it be I thought. Moreover uploading all my clothes took a while. But... it's actually pretty useful than I expected… The 'Daily Drip' is good for when my brain is fried. Although sometimes the AI suggests weird combos… but it has made me wear that one jacket I always forget I own. It's solid.",
    name: "Melbin Sibi",
    details: "Senior Director of Sales (Hey Hoku), Bangalore",
    bgColor: "#f0ead8",
  },
  {
    id: 4,
    review:
      "The pain of getting ready for the first date I was trying on everything I owned overthinking the vibe, should I go casual or should I wear partyish clothes. I finally just opened Hoku and tried the app. It gave me a casual yet effortless kinda look and I felt so much more confident walking out the door. Yes guys I got a second date lol! Thank you guys!!",
    name: "Siva Vishnu",
    details: "Music Director (YT @sivavishnu24), Chennai",
    bgColor: "#f0ead8",
  },
  {
    id: 5,
    review:
      "I've tried so many of these 'virtual closet' apps and they're all either super complicated or feel like they were made for my mom. Hoku is the first one that actually feels like... it gets it? The app is so cute, but the best part is that it talks to you. It's not just buttons. It's like 'what's the vibe today?' or 'okay, let's scan your closet.' It feels like texting a friend who has really good style, not using a 'utility.' Super easy to set up, and I actually want to open it in the morning.",
    name: "Sona I",
    details: "Amrita (BA Lit), Cochin",
    bgColor: "#f0ead8",
  },
];

export const teamsData = [
  {
    id: 1,
    name: "Swethaa Ajai",
    designation: "Co-Founder, COO & Community Manager",
    description: "My fashion expert. She knows what humans will actually wear.",
    image: "/landing-page/team/swethaa.jpg",
  },
  {
    id: 2,
    name: "Ajai Aravindh",
    designation: "Co-Founder & CEO",
    description: "He wrangles my cosmic vision into a mortal 'business plan.'",
    image: "/landing-page/team/ajai.jpg",
  },
  {
    id: 3,
    name: "Melbin Sibi",
    designation: "",
    description:
      "My strategy whisperer. He tells me what the humans actually want",
    image: "/landing-page/team/melbin.jpg",
  },
  {
    id: 5,
    name: "Surya Prakash",
    designation: "Graphic Designer & Marketing Assistant",
    description: "The vibe curator. He makes my timeless genius look fabulous.",
    image: "/landing-page/team/surya-prakash.jpg",
  },
];

export const faqData = [
  {
    id: 1,
    category: "Getting Started & Your Wardrobe",
    questions: [
      {
        q: "How does the AI Wardrobe Assistant know my clothes?",
        a: "Use the Hoku Cam to scan your items live, or simply upload photos. You can upload your clothes one by one and Hoku will add it to your digital wardrobe and remember it.",
      },
      {
        q: "Do I have to upload every single piece of clothing?",
        a: "No, you can start with key pieces. The more you add, the better the suggestions you get from your AI stylist!",
      },
      {
        q: "How does my AI personal stylist learn my taste?",
        a: "Hoku analyses your tastes based on your details provided during onboarding. Also Hoku analyzes your uploads and adapts to outfits you save, constantly refining your style.",
      },
    ],
  },
  {
    id: 2,
    category: "Daily Styling & Features",
    questions: [
      {
        q: "Why use Hoku instead of styling myself?",
        a: "Hoku ends decision fatigue. Get 3 perfect, ready-to-wear looks every single morning using Daily Drip. Hoku helps you when you're confused or want to make quicker decisions about what to style.",
      },
      {
        q: "What is the Look Lab feature for experimentation?",
        a: "It's a virtual studio to mix and match outfit combos for each individual outfit you own. You can explore new style matches for existing wardrobe items and unlock new combinations.",
      },
      {
        q: "Can my AI Wardrobe Assistant style a special event outfit?",
        a: "Yes! The Occasion station perfectly curates looks for weddings, interviews, and dates. You can just enter the when, the where and the what of the event and Hey Hoku will generate an outfit that matches your style, your vibe and your preferences.",
      },
      {
        q: "How does this AI personal stylist help me shop smarter?",
        a: "The Shopping Quest finds new items that perfectly match your existing wardrobe. If you want to expand your wardrobe, or explore new style, the suggestions from Hey Hoku will guide you.",
      },
      {
        q: "Can my AI Wardrobe Assistant rate my look?",
        a: "Low-key, yeah. Get style checks and better alternatives instantly.",
      },
      {
        q: "How does AI manage my wardrobe?",
        a: "Hey Hoku organises clothes into your digital wardrobe. You can create new Pods and customise them based on your preferences and style like you would organise a playlist on spotify.",
      },
      {
        q: "Where do all my matched outfits go?",
        a: "You get two default Pods when you sign up. 'It's a match' Pod contains all the matched outfits in one place and 'Favorites' Pod contains all your favourites. You can create new pods to organise your own styles.",
      },
      {
        q: "Does Hey Hoku promote sustainable fashion?",
        a: "100%. Hey Hoku helps you re-discover styles you already own before buying new. It encourages re-styling, reduces impulse shopping, and promotes mindful closet use.",
      },
      {
        q: "Can I see what other users are styling?",
        a: "In upcoming updates, the 'Style Pods' community feature will let you explore trending looks curated by creators across campuses and cities.",
      },
      {
        q: "Can Hey Hoku detect duplicate items?",
        a: "Yep — it automatically flags similar uploads so your wardrobe stays clean and clutter-free.",
      },
      {
        q: "Does Hey Hoku work for men too?",
        a: "Absolutely. While it's built around Gen Z fashion culture, the AI stylist adapts to all genders and style preferences.",
      },
    ],
  },
  {
    id: 3,
    category: "Privacy, Security, & Cost",
    questions: [
      {
        q: "Is my wardrobe and style data private and safe?",
        a: (<>Absolutely. Your data is protected with industry-standard security and is completely private. We at Hey Hoku follow strict guidelines to ensure your safety and privacy. You can read our <Link href="/legal/privacy" className={faqLink}>privacy policy</Link> here.</>) as ReactNode,
      },
      {
        q: "What if I remove clothes from my actual closet?",
        a: "Just delete the item in the wardrobe. Hoku instantly updates your styling options.",
      },
      {
        q: "How much does the AI Wardrobe Assistant service cost?",
        a: (<>Hey Hoku has affordable <Link href="/pricing" className={faqLink}>pricing plans</Link> so all users can experience the app. New users receive free Hoku Coins at sign-up to explore premium features. You can check Hey Hoku <Link href="/pricing" className={faqLink}>pricing plans</Link> here.</>) as ReactNode,
      },
      {
        q: "Is Hey Hoku available on both Android and iOS?",
        a: "Yes! You can access Hey Hoku from any browser. The AI Styling app is installable as a PWA shortcut when you visit heyhoku.ai. Native Android and iOS apps are on the roadmap.",
      },
      {
        q: "Do I need an internet connection for Hey Hoku to work?",
        a: "Yes, since outfit generation and wardrobe syncing happen through the cloud, you'll need to be online for the AI personal stylist to give you best results.",
      },
      {
        q: "How do I report a bug or share feedback?",
        a: (<>You can tap the feedback button in your profile or <Link href="/support" className={faqLink}>contact support</Link>. Your feedback directly shapes new feature drops.</>) as ReactNode,
      },
      {
        q: "What are Hoku Coins and how do I use them?",
        a: (<><Link href="/pricing" className={faqLink}>Hoku Coins</Link> are your in-app credits used for premium AI features. Daily Drip, Look Lab, and Image-Based Shopping are free! Occasion Station and Style Station cost 15 coins each. New users get free coins at sign-up to explore.</>) as ReactNode,
      },
      {
        q: "Will my credits expire?",
        a: "Nope. Your unused Hoku Coins stay valid and never expire.",
      },
      {
        q: "Does Hey Hoku sell my data to brands?",
        a: "Never. Your personal data stays private. We only share aggregated, anonymous insights (never your wardrobe details) for product trend analysis.",
      },
      {
        q: "Is my photo data used for AI training?",
        a: "No, your uploaded photos are only processed for your personal wardrobe and are not used to train external models.",
      },
    ],
  },
  {
    id: 4,
    category: "AI Accuracy & Personalization",
    questions: [
      {
        q: "What if I don't like the outfit suggestions from Hey Hoku?",
        a: "You can refine them! Just tap 'Dislike' button, Hoku learns your taste in real-time. Every feedback helps your AI stylist get smarter and closer to your true style.",
      },
      {
        q: "Does Hey Hoku consider my body type and skin tone?",
        a: "Yes. During onboarding, Hoku maps your body proportions and skin colour analysis to personalize suggestions that actually fit and flatter — not just look good in theory.",
      },
      {
        q: "Can I change my style preferences later?",
        a: "You can edit your profile later in the app but your style preferences will remain the same from your onboarding. Your accurate details in the onboarding will help Hey Hoku tailor the style suggestions accurately.",
      },
    ],
  },
  {
    id: 5,
    category: "Getting Started",
    questions: [
      {
        q: "Is Hey Hoku free to use?",
        a: (<>Many core features like Daily Drip, Look Lab, and Image-Based Shopping are completely free. Premium features use <Link href="/pricing" className={faqLink}>Hoku Coins</Link>, and new users get free coins at sign-up to explore everything.</>) as ReactNode,
      },
      {
        q: "Will my wardrobe data be kept safe?",
        a: "Yes! Your wardrobe, preferences, and Pods are stored securely and are always available whenever you sign in.",
      },
    ],
  },
];
